import type { Locale, LocaleDisplay } from '@icetus/contracts';
import { LOCALE_LABEL } from '@icetus/contracts';
import { localeMenuFor, LOCALE_DISPLAY_OPTIONS, localeVisible } from '../lib/locale-display';

/**
 * 页头语言入口的排布控件。
 *
 * 原来长在「站点信息 · 语言」卡片底部，2026-11-03 挪到「页头页脚 · 页头」：
 * 客户会到这一页来找"页头上有什么"，而语言入口**就是页头的一部分**
 * （和 Logo、主导航、页头按钮并排）。放在站点信息里时，他得先在脑子里
 * 知道"语言算站点设置"，才能想到去那儿找。
 *
 * 存的位置没变，还是 `sites.settings.localeDisplay` —— 控件搬家不等于数据搬家：
 * 站点信息里那份语言清单（开了哪几门、翻了多少）仍在那儿。
 *
 * 为什么是四档而不是"显示开关 + 布局"：见 `LocaleDisplay` 的注释。
 * 这一排按钮就是那段注释的可视形状，`不显示` 明确待在最后。
 */
export function LocaleModeRow({
  locales,
  defaultLocale,
  value,
  busy = false,
  editable = true,
  onChange,
}: {
  locales: Locale[];
  defaultLocale: Locale;
  value: LocaleDisplay;
  busy?: boolean;
  /**
   * 有没有 `site:update`。
   *
   * 编辑角色能改页头页脚的区块，但**改不了站点设置** —— 这一排按钮写的是
   * `sites.settings`，服务端会拦。没有权限时把按钮灰掉并说明白，
   * 比让客户点一下再看到一条 403 好：后者看起来像系统坏了。
   */
  editable?: boolean;
  onChange: (next: LocaleDisplay) => void;
}) {
  // 单语站没有这个入口，控件不出现：给一个"打开了也看不见"的开关是最难懂的界面
  if (locales.length < 2) return null;

  const visible = localeVisible(value);
  const asMenu = localeMenuFor(value, locales.length);
  const hint = LOCALE_DISPLAY_OPTIONS.find((opt) => opt.value === value)?.hint;

  return (
    <div className="langmode langmode--bar">
      <div className="langmode__head">
        <strong>语言切换</strong>
        {/*
          示意用母语写法，与前台一致；当前语言加粗，下拉只露它一个。
          关掉时不留空白格子 —— 画一个灰掉的"无"，比让这块突然消失更像"它还在"。
        */}
        <span className={`langmode__demo${asMenu ? ' is-menu' : ''}`} aria-hidden="true">
          {!visible ? (
            <span className="muted">前台不显示</span>
          ) : asMenu ? (
            <>
              <b>{LOCALE_LABEL[defaultLocale]}</b>
              <i className="langmode__caret" />
            </>
          ) : (
            locales.map((l) => (
              <span key={l} className={l === defaultLocale ? 'is-active' : undefined}>
                {LOCALE_LABEL[l]}
              </span>
            ))
          )}
        </span>
      </div>
      <div className="langmode__pick">
        <div className="segmented">
          {LOCALE_DISPLAY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={busy || !editable}
              className={`segmented__item${value === opt.value ? ' is-active' : ''}`}
              title={opt.hint}
              onClick={() => onChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <p className="langmode__hint muted">
        {editable ? hint : '当前角色只能看：改语言入口需要站点管理权限。'}
      </p>
    </div>
  );
}
